import { jwtDecode } from 'jwt-decode'

import { ACCESS_TOKEN_KEY } from '@/constants/app'
import { apiClient } from '@/lib/apiClient'

/**
 * Single place that owns the access token: storage plus the default
 * Authorization header. Passing `null` signs the session out.
 */
export const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    return
  }

  localStorage.removeItem(ACCESS_TOKEN_KEY)
  delete apiClient.defaults.headers.common.Authorization
}

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY)

const decodeBase64Url = (segment) => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/**
 * Reads the claims out of an access token.
 *
 * Two shapes are in play. The API's interim dev tokens are `payload.signature`
 * — two segments, no header — while the real JWTs that replace them will be
 * the standard `header.payload.signature`. Selecting the payload by segment
 * count rather than by a fixed index keeps both working; passing a two-segment
 * token to jwtDecode would decode the *signature* as the payload and every
 * session would look invalid.
 */
export const decodeAccessToken = (accessToken) => {
  if (!accessToken) return null

  const segments = accessToken.split('.')
  try {
    if (segments.length === 3) return jwtDecode(accessToken)
    if (segments.length === 2) return JSON.parse(decodeBase64Url(segments[0]))
    return null
  } catch {
    return null
  }
}

/** Rejects malformed and expired tokens without a network round trip. */
export const isAccessTokenValid = (accessToken) => {
  const claims = decodeAccessToken(accessToken)
  if (!claims) return false
  if (!claims.exp) return true
  return claims.exp > Date.now() / 1000
}
